/**
 * Shared API client.
 * API base: /api/v1 (proxied to localhost:3088 in dev via vite.config.ts).
 *
 * Auth: the session is an httpOnly cookie on the API origin, set by the Titanium
 * hosted login (API /auth/*). Every call sends it with `credentials: "include"`;
 * no token is ever stored in or read from localStorage.
 */

/** Origin of the API ("" in dev: same origin through the Vite proxy). */
export const API_ORIGIN: string = import.meta.env.VITE_API_URL ?? "";

const BASE = `${API_ORIGIN}/api/v1`;

/** Absolute URL of an API auth route (/auth/start, /auth/logout, ...). */
export function authUrl(path: `/auth/${string}`): string {
  return `${API_ORIGIN}${path}`;
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

/** Fired when the API answers 401, so the auth context can drop a dead session. */
export const UNAUTHENTICATED_EVENT = "aas:unauthenticated";

async function failure(res: Response): Promise<ApiError> {
  let message = `HTTP ${res.status}`;
  try {
    const j = await res.json();
    message = j.error ?? message;
  } catch {}
  if (res.status === 401) window.dispatchEvent(new Event(UNAUTHENTICATED_EVENT));
  return new ApiError(res.status, message);
}

async function call<T>(url: string, method: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw await failure(res);
  return res.json() as Promise<T>;
}

function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  return call<T>(`${BASE}${path}`, method, body);
}

// ─── Auth ─────────────────────────────────────────────────

export interface TenantInfo {
  _id: string;
  name: string;
  email: string;
  plan: string;
}

export interface SessionInfo {
  tenant: TenantInfo;
  email: string;
  method: "titanium" | "password";
}

export interface AuthConfig {
  legacyPasswordLogin: boolean;
  openSignup: boolean;
}

/** Legacy password login answer (LEGACY_PASSWORD_LOGIN=on only); the session is the cookie. */
export interface LoginResponse {
  tenant: TenantInfo;
}

export const authApi = {
  /** The signed-in session, or null when there is none (401). */
  session: async (): Promise<SessionInfo | null> => {
    const res = await fetch(authUrl("/auth/session"), { credentials: "include" });
    if (res.status === 401) return null;
    if (!res.ok) throw await failure(res);
    return res.json() as Promise<SessionInfo>;
  },
  config: (): Promise<AuthConfig> => call<AuthConfig>(authUrl("/auth/config"), "GET"),
  /** Ask the API to email a Titanium sign-in link. Same answer for every address. */
  sendMagicLink: (email: string): Promise<{ ok: true }> =>
    call<{ ok: true }>(authUrl("/auth/magic-link"), "POST", { email }),
};

/** LEGACY (LEGACY_PASSWORD_LOGIN=on): password login. 404 otherwise. */
export async function login(email: string, password: string): Promise<LoginResponse> {
  return request<LoginResponse>("POST", "/auth/login", { email, password });
}

/** LEGACY (LEGACY_PASSWORD_LOGIN=on): change password. 404 otherwise. */
export async function resetPassword(email: string, currentPassword: string, newPassword: string): Promise<{ success: boolean }> {
  return request<{ success: boolean }>("POST", "/auth/reset-password", { email, currentPassword, newPassword });
}

// ─── Sites ────────────────────────────────────────────────

export interface SiteStats {
  pageCount: number;
  totalVisitors: number;
  monthlyRevenue: number;
  lastBuildAt: string | null;
}

export interface AwinAdvertiser {
  advertiserId: string;
  name?: string;
}

export interface SiteAwinConfig {
  publisherId?: string;
  advertisers?: AwinAdvertiser[];
}

export interface SiteCjAdvertiser {
  advertiserId: string;
  name?: string;
}

export interface SiteCjConfig {
  websiteId?: string;
  advertisers?: SiteCjAdvertiser[];
}

export interface SiteAffiliateConfig {
  ebay?: { campaignId?: string; publisherId?: string; region?: string };
  amazon?: { trackingId?: string; region?: string };
  awin?: SiteAwinConfig;
  cj?: SiteCjConfig;
  [key: string]: Record<string, string | undefined> | SiteAwinConfig | SiteCjConfig | undefined;
}

export type SiteTheme = "forest" | "ocean" | "sunset" | "slate" | "royal" | "crimson" | "midnight" | "sand";
export type SiteFontStyle = "modern" | "editorial" | "classic" | "minimal" | "technical";

export interface SiteConfig {
  title: string;
  description: string;
  niche: string;
  affiliatePrograms: string[];
  interlinks: string[];
  aiModel?: string;
  llmsTxt?: boolean;
  theme?: SiteTheme;
  fontStyle?: SiteFontStyle;
  affiliateOverrides?: SiteAffiliateConfig;
  gaPropertyId?: string;
  siteType?: "affiliate" | "directory" | "blog" | "informational" | "utility";
  features?: string[];
  contentSettings?: {
    defaultWordCount?: number;
    tone?: "professional" | "casual" | "technical";
  };
  sourceUrl?: string;
  gaMeasurementId?: string;
  gaTrackingId?: string;
  navLinks?: { label: string; href: string }[];
  wpUsername?: string;
  wpAppPassword?: string;
  dnsStatus?: {
    configured: boolean;
    zoneId?: string;
    zoneStatus?: string;
    nameServers?: string[];
    pagesProject?: string;
    error?: string;
    updatedAt?: string;
  };
}

export interface Site {
  _id: string;
  tenantId: string;
  domain: string;
  clusterId?: string;
  status: "active" | "inactive" | "building" | "error" | "pending";
  config: SiteConfig;
  stats: SiteStats;
}

export interface SiteListResponse {
  data: Site[];
  total: number;
}

export interface DiscoveredUrl {
  url: string;
  slug: string;
  lastSeen?: string;
  source: "sitemap" | "cdx" | "404-log";
  statusCode?: number;
}

export interface DiscoverResult {
  domain: string;
  urls: DiscoveredUrl[];
  total: number;
  sources: string[];
}

export interface DnsSetupResult {
  zoneId: string;
  nameServers: string[];
  records: { id: string; type: string; name: string; content: string; proxied: boolean }[];
  pagesProjectName: string;
  isNewZone: boolean;
}

export interface ClusterRecommendation {
  kind: "existing-cluster" | "new-cluster";
  clusterId?: string;
  clusterName: string;
  confidence: number;
  reason: string;
  suggestedRole: "hub" | "spoke";
}

export interface CloudflareImportCandidate {
  zoneId: string;
  domain: string;
  status: string;
  nameServers: string[];
  alreadyImported: boolean;
  existingSiteId?: string;
  recommendation: ClusterRecommendation;
}

export interface CloudflareImportListResponse {
  connected: boolean;
  needsIntegration: boolean;
  data: CloudflareImportCandidate[];
  total: number;
}

export interface CloudflareImportCreateResponse {
  data: Site;
  imported: boolean;
  recommendation: ClusterRecommendation;
  clusterCreated: boolean;
}

export const sites = {
  list: () => request<SiteListResponse>("GET", "/sites"),
  get: (id: string) => request<{ data: Site }>("GET", `/sites/${id}`),
  create: (body: Partial<Site>) => request<{ data: Site }>("POST", "/sites", body),
  update: (id: string, patch: Partial<Site>) =>
    request<{ data: Site }>("PATCH", `/sites/${id}`, patch),
  delete: (id: string) => request<{ success: boolean }>("DELETE", `/sites/${id}`),
  setupDns: (id: string) => request<{ data: DnsSetupResult }>("POST", `/sites/${id}/setup-dns`),
  clearContent: (id: string) => request<{ data: { deleted: number } }>("DELETE", `/sites/${id}/content`),
  discoverUrls: (id: string, opts?: { cdx?: boolean }) =>
    request<{ data: DiscoverResult }>("GET", `/sites/${id}/discover-urls${opts?.cdx ? "?cdx=true" : ""}`),
  importDiscoveredUrls: (id: string, slugs: string[]) =>
    request<{ data: { created: number; skipped: number; errors: number; total: number } }>(
      "POST", `/sites/${id}/import-discovered-urls`, { slugs }
    ),
  listCloudflareImports: () => request<CloudflareImportListResponse>("GET", "/sites/imports/cloudflare"),
  importFromCloudflare: (domain: string, clusterId?: string, newClusterName?: string) =>
    request<CloudflareImportCreateResponse>("POST", "/sites/imports/cloudflare", {
      domain,
      ...(clusterId ? { clusterId } : {}),
      ...(newClusterName ? { newClusterName } : {}),
    }),
};

// ─── Clusters ─────────────────────────────────────────────

export type InterlinkStrategy = "hub-and-spoke" | "full-mesh" | "manual" | "mesh" | "linear" | "star";

export interface Cluster {
  _id: string;
  tenantId: string;
  name: string;
  hubDomain: string;
  spokeDomains: string[];
  interlinkStrategy: InterlinkStrategy;
}

export interface ClusterPlannerDomain {
  domain: string;
  preferred: boolean;
  available: boolean | null;
  purchasable: boolean | null;
}

export interface ClusterPlannerIdea {
  slug: string;
  title: string;
  angle: string;
  reason: string;
  audience: string;
  domains: ClusterPlannerDomain[];
}

export interface ClusterPlanner {
  audience: string;
  themes: string[];
  ideas: ClusterPlannerIdea[];
  cloudflareConnected: boolean;
}

export interface ClusterListResponse {
  data: Cluster[];
  total: number;
}

export const clusters = {
  list: () => request<ClusterListResponse>("GET", "/clusters"),
  get: (id: string) => request<{ data: Cluster }>("GET", `/clusters/${id}`),
  planner: (id: string) => request<{ data: ClusterPlanner }>("GET", `/clusters/${id}/planner`),
  create: (body: Partial<Cluster>) => request<{ data: Cluster }>("POST", "/clusters", body),
  update: (id: string, patch: Partial<Cluster>) =>
    request<{ data: Cluster }>("PATCH", `/clusters/${id}`, patch),
  delete: (id: string) => request<{ success: boolean }>("DELETE", `/clusters/${id}`),
  addSpoke: (id: string, domain: string) =>
    request<{ success: boolean }>("POST", `/clusters/${id}/spokes`, { domain }),
  removeSpoke: (id: string, domain: string) =>
    request<{ success: boolean }>("DELETE", `/clusters/${id}/spokes/${encodeURIComponent(domain)}`),
  setHub: (id: string, domain: string) =>
    request<{ data: Cluster }>("POST", `/clusters/${id}/set-hub`, { domain }),
};

// ─── Businesses ──────────────────────────────────────────────

export type BusinessStatus = "active" | "archived" | "planning";

export interface Business extends Cluster {
  description?: string;
  niche?: string;
  targetAudience?: string;
  brandVoice?: string;
  sharedAffiliateConfig?: Record<string, unknown>;
  emailConfig?: {
    provider?: string;
    warmupStatus?: string;
    dailyCapPerContact?: number;
    weeklyCapPerContact?: number;
    enabled?: boolean;
  };
  revenueTracking?: { monthlyAffiliate?: number; monthlyEmail?: number };
  status?: BusinessStatus;
}

export const businesses = {
  list: () => request<{ data: Business[]; total: number }>("GET", "/businesses"),
  get: (id: string) => request<{ data: Business }>("GET", `/businesses/${id}`),
  create: (body: Partial<Business>) => request<{ data: Business }>("POST", "/businesses", body),
  update: (id: string, patch: Partial<Business>) =>
    request<{ data: Business }>("PATCH", `/businesses/${id}`, patch),
  delete: (id: string) => request<{ success: boolean }>("DELETE", `/businesses/${id}`),
  addSpoke: (id: string, domain: string) =>
    request<{ success: boolean }>("POST", `/businesses/${id}/spokes`, { domain }),
  removeSpoke: (id: string, domain: string) =>
    request<{ success: boolean }>("DELETE", `/businesses/${id}/spokes/${encodeURIComponent(domain)}`),
  setHub: (id: string, domain: string) =>
    request<{ data: Business }>("POST", `/businesses/${id}/set-hub`, { domain }),
};

// ─── Pages ────────────────────────────────────────────────

export interface QAItem {
  id?: string;
  question: string;
  answer: string;
  askedAt: string;
  ipHash?: string;
  helpful?: number;
  approved?: boolean;
}

export interface PageFrontmatter {
  title: string;
  description: string;
  keywords: string[];
  products?: string[];
  interlinks?: { site: string; slug: string; text: string }[];
}

export interface Page {
  _id: string;
  siteId: string;
  slug: string;
  content: string;
  frontmatter: PageFrontmatter;
  status?: "draft" | "review" | "published";
  reviewNotes?: string;
  publishedAt?: string;
  qa?: QAItem[];
  createdAt: string;
  updatedAt: string;
}

export interface PageListResponse {
  data: Page[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface BulkPublishQueueResult {
  queued: { pageId: string; taskId: string; slug: string }[];
  queuedCount: number;
  skipped: number;
}

export const pages = {
  list: (siteId: string, page = 1, pageSize = 50) =>
    request<PageListResponse>("GET", `/pages?siteId=${siteId}&page=${page}&pageSize=${pageSize}`),
  get: (id: string) => request<{ data: Page }>("GET", `/pages/${id}`),
  create: (body: Partial<Page>) => request<{ data: Page }>("POST", "/pages", body),
  update: (id: string, patch: Partial<Page>) =>
    request<{ data: Page }>("PATCH", `/pages/${id}`, patch),
  delete: (id: string) => request<{ success: boolean }>("DELETE", `/pages/${id}`),
  review: (id: string) => request<{ data: Page }>("POST", `/pages/${id}/review`),
  publish: (id: string) => request<{ data: Page }>("POST", `/pages/${id}/publish`),
  bulkPublishQueue: (siteId: string, pageIds: string[], instructions?: string) =>
    request<{ data: BulkPublishQueueResult }>("POST", "/pages/bulk-publish-queue", {
      siteId,
      pageIds,
      ...(instructions ? { instructions } : {}),
    }),
};

// ─── Affiliates ───────────────────────────────────────────

export interface AffiliateConfig {
  ebay: {
    appId: string;
    certId?: string;
    devId?: string;
    networkId?: string;
    publisherId?: string;
  } | null;
  amazon: {
    accessKey: string;
    secretKey: string;
    region?: string;
  } | null;
  awin: {
    publisherId: string;
    apiToken?: string;
    advertiserId?: string;
    region?: string;
  } | null;
  cj: {
    cid: string;
    apiKey: string;
    advertiserId?: string;
  } | null;
  custom: {
    name: string;
    baseUrl: string;
    affiliateId: string;
    commissionRate?: number;
  }[];
}

export type LiveProductNetwork = "ebay" | "amazon" | "awin" | "cj";

export interface LiveProduct {
  externalId: string;
  title: string;
  price?: number;
  currency?: string;
  imageUrl?: string;
  productUrl: string;
  affiliateUrl?: string;
  merchantName?: string;
  merchantId?: string;
  category?: string;
  availability?: string;
  condition?: string;
  network: LiveProductNetwork;
}

export interface AwinProgram {
  id: string;
  name: string;
  displayUrl: string;
  primaryRegion: string;
  membershipStatus: string;
  primarySector: string;
  logoUrl?: string;
  clickThroughUrl?: string;
}

export interface CjAdvertiser {
  id: string;
  name: string;
  primaryRegion: string;
  programTerms?: string;
  logoUrl?: string;
  clickCommission?: string;
}

export interface ProductFeedFilter {
  advertiserIds?: string[];
  regions?: string[];
  languages?: string[];
  verticals?: string[];
  membershipStatuses?: string[];
  search?: string;
  limitFeeds?: number;
  limitProducts?: number;
}

export interface SiteProductFeed {
  id: string;
  provider: "awin";
  name: string;
  enabled: boolean;
  sourceUrl: string;
  sourceKind: "awin-feed-list" | "csv" | "csv.gz";
  filters?: ProductFeedFilter;
  lastSyncedAt?: string;
  lastSyncStatus?: "idle" | "running" | "success" | "error";
  lastSyncSummary?: {
    feedsDiscovered: number;
    feedsSynced: number;
    productsUpserted: number;
    productsDeleted: number;
    errors: string[];
  };
}

export interface AffiliateProduct {
  _id: string;
  tenantId: string;
  siteId: string;
  provider: LiveProductNetwork;
  promoted?: boolean;
  sourceFeedId: string;
  sourceFeedUrl?: string;
  merchantId?: string;
  merchantName?: string;
  externalId: string;
  sku?: string;
  title: string;
  description?: string;
  brand?: string;
  category?: string;
  availability?: string;
  condition?: string;
  currency?: string;
  priceAmount?: number;
  salePriceAmount?: number;
  productUrl?: string;
  affiliateUrl?: string;
  imageUrl?: string;
  additionalImageUrls?: string[];
  gtin?: string;
  mpn?: string;
  importedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProductFeedSyncResult {
  provider: LiveProductNetwork;
  feedsDiscovered: number;
  feedsSynced: number;
  productsUpserted: number;
  productsDeleted: number;
  errors: string[];
}

export const affiliates = {
  get: () => request<{ data: AffiliateConfig }>("GET", "/affiliates"),
  updateEbay: (body: NonNullable<AffiliateConfig["ebay"]>) =>
    request<{ success: boolean }>("PUT", "/affiliates/ebay", body),
  updateAmazon: (body: NonNullable<AffiliateConfig["amazon"]>) =>
    request<{ success: boolean }>("PUT", "/affiliates/amazon", body),
  updateAwin: (body: AffiliateConfig["awin"]) =>
    request<{ success: boolean }>("PUT", "/affiliates/awin", body),
  updateCj: (body: AffiliateConfig["cj"]) =>
    request<{ success: boolean }>("PUT", "/affiliates/cj", body),
  addCustom: (body: AffiliateConfig["custom"][0]) =>
    request<{ success: boolean }>("POST", "/affiliates/custom", body),
  removeCustom: (name: string) =>
    request<{ success: boolean }>("DELETE", `/affiliates/custom/${encodeURIComponent(name)}`),
  liveSearch: (network: LiveProductNetwork, q: string, limit = 24) =>
    request<{ data: LiveProduct[]; total: number }>(
      "GET",
      `/affiliates/live-search?network=${network}&q=${encodeURIComponent(q)}&limit=${limit}`
    ),
  listAwinPrograms: () =>
    request<{ data: AwinProgram[]; total: number }>("GET", "/affiliates/programs/awin"),
  listCjAdvertisers: () =>
    request<{ data: CjAdvertiser[]; total: number }>("GET", "/affiliates/programs/cj"),
  listPromoted: (network?: LiveProductNetwork) => {
    const qs = network ? `?network=${network}` : "";
    return request<{ data: AffiliateProduct[]; total: number }>("GET", `/affiliates/promoted${qs}`);
  },
  promote: (product: LiveProduct, sourceFeedId?: string) =>
    request<{ success: boolean }>("POST", "/affiliates/promoted", { ...product, sourceFeedId }),
  unpromote: (externalId: string, network: LiveProductNetwork) =>
    request<{ success: boolean }>(
      "DELETE",
      `/affiliates/promoted/${encodeURIComponent(externalId)}?network=${network}`
    ),
  listProductFeeds: (siteId: string) =>
    request<{ data: SiteProductFeed[] }>("GET", `/affiliates/sites/${siteId}/product-feeds`),
  createProductFeed: (siteId: string, body: Partial<SiteProductFeed>) =>
    request<{ data: SiteProductFeed[] }>("POST", `/affiliates/sites/${siteId}/product-feeds`, body),
  updateProductFeed: (siteId: string, feedId: string, body: Partial<SiteProductFeed>) =>
    request<{ data: SiteProductFeed[] }>("PATCH", `/affiliates/sites/${siteId}/product-feeds/${feedId}`, body),
  deleteProductFeed: (siteId: string, feedId: string) =>
    request<{ data: SiteProductFeed[] }>("DELETE", `/affiliates/sites/${siteId}/product-feeds/${feedId}`),
  syncProductFeed: (siteId: string, feedId: string) =>
    request<{ data: ProductFeedSyncResult }>("POST", `/affiliates/sites/${siteId}/product-feeds/${feedId}/sync`),
  syncAllProductFeeds: (siteId: string) =>
    request<{ data: ProductFeedSyncResult[] }>("POST", `/affiliates/sites/${siteId}/product-feeds/sync`),
  listProducts: (siteId: string, opts?: { provider?: LiveProductNetwork; q?: string; limit?: number }) => {
    const params = new URLSearchParams();
    if (opts?.provider) params.set("provider", opts.provider);
    if (opts?.q) params.set("q", opts.q);
    if (opts?.limit) params.set("limit", String(opts.limit));
    const qs = params.toString();
    return request<{ data: AffiliateProduct[]; total: number }>(
      "GET",
      `/affiliates/sites/${siteId}/products${qs ? `?${qs}` : ""}`
    );
  },
};

// ─── Setup ───────────────────────────────────────────────

export interface SetupStatus {
  needsSetup: boolean;
}

export const setup = {
  status: () => request<SetupStatus>("GET", "/setup/status"),
  createAdmin: (body: { name: string; email: string; password: string }) =>
    request<LoginResponse>("POST", "/setup/create-admin", body),
};

// ─── Settings / Claude OAuth ─────────────────────────────

export interface TokenStatus {
  hasCredentials: boolean;
  valid: boolean;
  expiresAt: number | null;
}

export interface CliCheck {
  installed: boolean;
  version?: string;
}

export interface ClaudeLoginStatus {
  status: "idle" | "waiting_for_auth" | "success" | "error";
  message?: string;
  authUrl?: string;
  authCode?: string;
  error?: string;
}

export interface GeminiLoginStatus {
  status: "idle" | "waiting_for_auth" | "success" | "error";
  message?: string;
  authUrl?: string;
  error?: string;
}

export interface CodexStatus {
  hasCredentials: boolean;
  valid: boolean;
  expiresAt: number | null;
  authMode?: string | null;
}

export interface CodexLoginStatus {
  status: "idle" | "waiting_for_auth" | "success" | "error";
  message?: string;
  authUrl?: string;
  authCode?: string;
  error?: string;
}

export const settings = {
  tokenStatus: () => request<TokenStatus>("GET", "/settings/token-status"),
  refreshToken: () => request<{ success: boolean; expiresAt: number }>("POST", "/settings/refresh-token"),
  checkCli: () => request<CliCheck>("GET", "/settings/check-cli"),
  claudeLogin: () => request<ClaudeLoginStatus>("POST", "/settings/claude-login"),
  claudeLoginStatus: () => request<ClaudeLoginStatus>("GET", "/settings/claude-login/status"),
  claudeLoginCancel: () => request<{ success: boolean }>("POST", "/settings/claude-login/cancel"),
  claudeDisconnect: () => request<{ success: boolean }>("POST", "/settings/claude-disconnect"),
  geminiCheck: () => request<CliCheck>("GET", "/settings/gemini-check"),
  geminiStatus: () => request<{ authenticated: boolean }>("GET", "/settings/gemini-status"),
  geminiLogin: () => request<GeminiLoginStatus>("POST", "/settings/gemini-login"),
  geminiLoginStatus: () => request<GeminiLoginStatus>("GET", "/settings/gemini-login/status"),
  geminiLoginCancel: () => request<{ success: boolean }>("POST", "/settings/gemini-login/cancel"),
  codexCheck: () => request<CliCheck>("GET", "/settings/codex-check"),
  codexStatus: () => request<CodexStatus>("GET", "/settings/codex-status"),
  codexLogin: () => request<CodexLoginStatus>("POST", "/settings/codex-login"),
  codexLoginStatus: () => request<CodexLoginStatus>("GET", "/settings/codex-login/status"),
  codexLoginCancel: () => request<{ success: boolean }>("POST", "/settings/codex-login/cancel"),
  codexDisconnect: () => request<{ success: boolean }>("POST", "/settings/codex-disconnect"),
  getCredentials: () => request<{ data: Record<string, Record<string, string | boolean>> }>("GET", "/settings/credentials"),
  saveCredentials: (provider: string, body: Record<string, string>) =>
    request<{ success: boolean }>("PUT", `/settings/credentials/${provider}`, body),
  deleteCredentials: (provider: string) =>
    request<{ success: boolean }>("DELETE", `/settings/credentials/${provider}`),
};

// ─── Team Users ──────────────────────────────────────────

export type TenantUserRole = "owner" | "admin" | "editor" | "viewer";

export interface TenantUser {
  _id: string;
  tenantId: string;
  name: string;
  email: string;
  role: TenantUserRole;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

export const users = {
  list: () => request<{ data: TenantUser[] }>("GET", "/users"),
  create: (body: { name: string; email: string; password: string; role?: TenantUserRole }) =>
    request<{ data: TenantUser }>("POST", "/users", body),
  update: (id: string, patch: { name?: string; email?: string; password?: string; role?: TenantUserRole }) =>
    request<{ data: TenantUser }>("PATCH", `/users/${id}`, patch),
  delete: (id: string) => request<{ success: boolean }>("DELETE", `/users/${id}`),
};

// ─── Agents ───────────────────────────────────────────────

export type AgentRole = "orchestrator" | "worker" | "quality_control";
export type AgentStatus = "idle" | "working" | "offline";

export interface AgentSpec {
  identity?: { personality?: string; tone?: string; rules?: string[] };
  config?: { role_description?: string; capabilities?: string[]; boundaries?: string[] };
  guardrails?: { allowed_actions?: string[]; forbidden_actions?: string[]; escalation_rules?: string[] };
}

export interface Agent {
  _id: string;
  tenantId: string;
  name: string;
  role: AgentRole;
  status: AgentStatus;
  systemPrompt: string;
  defaultModel?: string;
  currentTaskId?: string | null;
  lastActiveAt?: string;
  spec?: AgentSpec;
  createdAt: string;
  updatedAt: string;
}

export const agents = {
  list: (role?: AgentRole) =>
    request<{ data: Agent[] }>("GET", role ? `/agents?role=${role}` : "/agents"),
  get: (id: string) => request<{ data: Agent }>("GET", `/agents/${id}`),
  create: (body: Partial<Agent>) => request<{ data: Agent }>("POST", "/agents", body),
  update: (id: string, patch: Partial<Agent>) =>
    request<{ data: Agent }>("PATCH", `/agents/${id}`, patch),
  delete: (id: string) => request<{ success: boolean }>("DELETE", `/agents/${id}`),
};

// ─── Tasks (formerly Agent Tasks) ─────────────────────────

export type TaskType =
  | "keyword-research"
  | "content-generation"
  | "site-audit"
  | "strategy"
  | "site-scrape"
  | "competitor-analysis"
  | "link-check"
  | "meta-optimization"
  | "build-website"
  | "site-deploy"
  | "dns-update"
  | "qc-review"
  | "image-generation"
  | "seo-audit"
  | "crawl-check"
  | "affiliate-fix"
  | "url-discovery"
  | "affiliate-product-sync";

export type TaskStatus = "todo" | "in_progress" | "done" | "failed" | "waiting";
export type TaskPriorityLabel = "critical" | "high" | "normal" | "low";

export interface TaskProcessStep {
  name: string;
  description?: string;
  status: "pending" | "in_progress" | "done" | "failed" | "skipped";
  completedAt?: string;
}

export interface TaskQCItem {
  label: string;
  passed: boolean;
  note?: string;
}

export interface Task {
  _id: string;
  tenantId: string;
  siteId?: string;
  title: string;
  description?: string;
  type: TaskType;
  status: TaskStatus;
  priority: number;
  priorityLabel: TaskPriorityLabel;
  assignedAgentId?: string | null;
  createdBy: string;
  templateId?: string | null;
  parentTaskId?: string | null;
  context: Record<string, unknown>;
  result?: Record<string, unknown>;
  error?: string;
  process?: TaskProcessStep[];
  qcChecklist?: TaskQCItem[];
  qcStatus?: "passed" | "failed" | "pending";
  requiresApproval: boolean;
  startedAt?: string;
  completedAt?: string;
  // Recurring task fields
  cronExpression?: string | null;
  enabled?: boolean;
  nextRun?: string | null;
  lastRun?: string | null;
  runCount?: number;
  createdAt: string;
  updatedAt: string;
}

/** @deprecated Use Task */
export type AgentTask = Task;
/** @deprecated Use TaskType */
export type ScheduledTaskType = TaskType;

export const agentTasks = {
  get: (id: string) => request<{ data: Task }>("GET", `/agent-tasks/${id}`),
  list: (siteId: string, filters?: { type?: TaskType; status?: string; agentId?: string }) => {
    let url = `/agent-tasks?siteId=${siteId}`;
    if (filters?.type) url += `&type=${filters.type}`;
    if (filters?.status) url += `&status=${filters.status}`;
    if (filters?.agentId) url += `&agentId=${filters.agentId}`;
    return request<{ data: Task[] }>("GET", url);
  },
  create: (body: {
    siteId: string;
    type: TaskType;
    context: Record<string, unknown>;
    title?: string;
    templateId?: string;
    assignedAgentId?: string;
    priority?: number;
    requiresApproval?: boolean;
  }) => request<{ data: Task }>("POST", "/agent-tasks", body),
  execute: (id: string) =>
    request<{ data: Task }>("POST", `/agent-tasks/${id}/execute`),
  generateContent: (body: { siteId: string; keyword: string; contentType?: string }) =>
    request<{ data: Task; pageId?: string }>("POST", "/agent-tasks/generate-content", body),
  runPipeline: (body: { siteId: string; maxPages?: number; keywords?: string[] }) =>
    request<{ data: { generated: number; failed: number; pages: { slug: string; title: string; contentType: string }[] } }>(
      "POST", "/agent-tasks/run-pipeline", body
    ),
  classifyKeyword: (keyword: string) =>
    request<{ keyword: string; contentType: string }>("GET", `/agent-tasks/classify-keyword?keyword=${encodeURIComponent(keyword)}`),
  approve: (id: string) =>
    request<{ data: Task }>("POST", `/agent-tasks/${id}/approve`),
  reject: (id: string, feedback: string) =>
    request<{ data: Task }>("POST", `/agent-tasks/${id}/reject`, { feedback }),
  scrapeSite: (body: { siteId: string; url?: string; limit?: number }) =>
    request<{ data: Task }>("POST", "/agent-tasks/scrape-site", body),
  buildWebsite: (siteId: string) =>
    request<{ data: Task }>("POST", "/agent-tasks/build-website", { siteId }),
  resumeBuildWebsite: (taskId: string) =>
    request<{ data: Task; resumed: boolean; completedSteps: number }>("POST", "/agent-tasks/build-website/resume", { taskId }),
  fixSite: (siteId: string) =>
    request<{ data: Task }>("POST", "/agent-tasks/fix-site", { siteId }),
  importContent: (siteId: string, options?: { sourceUrl?: string; limit?: number; status?: "draft" | "published" }) =>
    request<{ data: Task }>("POST", "/agent-tasks/import-content", { siteId, ...options }),
  importPage: (siteId: string, pageId: string, sourceUrl: string) =>
    request<{ data: { pageId: string; slug: string; wordCount: number; title: string } }>(
      "POST", "/agent-tasks/import-page", { siteId, pageId, sourceUrl }
    ),
  importWP: (siteId: string, options: {
    sourceUrl: string;
    username?: string;
    appPassword?: string;
    contentTypes?: ("posts" | "pages")[];
    limit?: number;
  }) => request<{ data: Task }>("POST", "/agent-tasks/import-wp", { siteId, ...options }),
  createWebsite: (data: { domain: string; niche: string; sourceUrl?: string; businessId?: string; affiliatePrograms?: string[] }) =>
    request<{ task: unknown; site: unknown }>("POST", "/agent-tasks/create-website", data),
  discoverTaxonomy: (siteId: string) =>
    request<unknown>("POST", "/agent-tasks/discover-taxonomy", { siteId }),
  astroDeploy: (siteId: string) =>
    request<unknown>("POST", "/agent-tasks/astro-deploy", { siteId }),
  getRegenerationProgress: (siteId: string) =>
    request<{ total: number; imported: number; queued: number; regenerating: number; regenerated: number; failed: number; published: number }>("GET", `/agent-tasks/regeneration-progress/${siteId}`),
  startRegeneration: (siteId: string) =>
    request<{ enqueued: number; taskId: string }>("POST", "/agent-tasks/start-regeneration", { siteId }),
  importWPXml: async (siteId: string, file: File, contentTypes: string[]) => {
    const form = new FormData();
    form.append("siteId", siteId);
    form.append("contentTypes", contentTypes.join(","));
    form.append("file", file);
    const res = await fetch(`${BASE}/agent-tasks/import-wp-xml`, {
      method: "POST",
      credentials: "include",
      body: form,
    });
    if (!res.ok) {
      let message = `HTTP ${res.status}`;
      try { const j = await res.json(); message = j.error ?? message; } catch {}
      throw new ApiError(res.status, message);
    }
    return res.json() as Promise<{ data: { imported: number; skipped: number; skippedEmpty: number; skippedDuplicate: number; errors: string[]; slugs: string[]; totalInFile: number } }>;
  },
};

// ─── Task Templates ───────────────────────────────────────

export type ActionCategory = "audit" | "fix" | "analysis" | "content" | "deploy" | "general";

export interface TaskTemplate {
  _id: string;
  tenantId: string;
  name: string;
  description?: string;
  type: TaskType;
  defaultAgentId?: string | null;
  model?: string;
  objective: string;
  promptTemplate: string;
  qcChecklist?: TaskQCItem[];
  processSteps?: { name: string; description?: string }[];
  maxRetries: number;
  timeoutSeconds: number;
  isActive: boolean;
  // Action UI fields
  category?: ActionCategory;
  icon?: string;
  color?: string;
  actionEndpoint?: string;
  actionMethod?: "GET" | "POST";
  sortOrder?: number;
  // Scheduling defaults
  cronExpression?: string | null;
  defaultEnabled?: boolean;
  createdAt: string;
  updatedAt: string;
}

export const taskTemplates = {
  list: (opts?: { type?: TaskType; category?: ActionCategory }) => {
    const params = new URLSearchParams();
    if (opts?.type) params.set("type", opts.type);
    if (opts?.category) params.set("category", opts.category);
    const qs = params.toString();
    return request<{ data: TaskTemplate[] }>("GET", qs ? `/task-templates?${qs}` : "/task-templates");
  },
  get: (id: string) => request<{ data: TaskTemplate }>("GET", `/task-templates/${id}`),
  create: (body: Partial<TaskTemplate>) =>
    request<{ data: TaskTemplate }>("POST", "/task-templates", body),
  update: (id: string, patch: Partial<TaskTemplate>) =>
    request<{ data: TaskTemplate }>("PATCH", `/task-templates/${id}`, patch),
  delete: (id: string) => request<{ success: boolean }>("DELETE", `/task-templates/${id}`),
};

// ─── Claude / AI Studio ───────────────────────────────────

export interface GenerateRequest {
  siteId: string;
  keywords: string[];
  model?: string;
}

export interface GenerateResponse {
  jobId: string;
  status: "queued" | "running" | "done" | "error";
  message?: string;
}

// ─── Analytics (Google Analytics) ─────────────────────────

export interface GAConnectionStatus {
  connected: boolean;
  accountId?: string | null;
  accountName?: string | null;
  propertyId?: string | null;
  connectedAt?: string;
}

export interface GAProperty {
  name: string;
  displayName: string;
}

export interface GAAccount {
  name: string;
  displayName: string;
  properties: GAProperty[];
}

export interface GAOverview {
  totalUsers: number;
  totalSessions: number;
  totalPageviews: number;
  avgBounceRate: number;
  avgSessionDuration: number;
}

export interface GAMetricRow {
  date: string;
  sessions: number;
  users: number;
  pageviews: number;
  bounceRate: number;
  avgSessionDuration: number;
}

export interface GATopPage {
  path: string;
  title: string;
  pageviews: number;
  users: number;
}

export interface GATrafficSource {
  source: string;
  medium: string;
  sessions: number;
  users: number;
}

function analyticsQuery(endpoint: string, days?: number, siteId?: string): string {
  const params = new URLSearchParams();
  if (days) params.set("days", String(days));
  if (siteId) params.set("siteId", siteId);
  const qs = params.toString();
  return `/analytics/${endpoint}${qs ? `?${qs}` : ""}`;
}

export interface SiteVisitors {
  sites: {
    siteId: string;
    domain: string;
    propertyId: string | null;
    currentMonth: number;
    lastMonth: number;
  }[];
  totals: { currentMonth: number; lastMonth: number };
}

export interface CombinedAnalytics {
  overview: GAOverview;
  daily: { date: string; users: number; sessions: number; pageviews: number }[];
  trafficSources: { source: string; sessions: number; users: number }[];
  topPages: { domain: string; path: string; title: string; pageviews: number; users: number }[];
  sitesIncluded: number;
}

// ─── Affiliate Click Analytics ──────────────────────────────

export interface AffiliateClickStats {
  totalClicks: number;
  topPages: { slug: string; clicks: number }[];
  topNetworks: { network: string; clicks: number }[];
  recentClicks: { slug: string; network: string; productRef: string; ts: string }[];
}

export const affiliateClickStats = {
  get: (siteId: string, days = 30) =>
    request<{ data: AffiliateClickStats }>("GET", `/analytics/affiliate-clicks?siteId=${siteId}&days=${days}`),
};

export const analytics = {
  status: () => request<GAConnectionStatus>("GET", "/analytics/status"),
  siteVisitors: () => request<{ data: SiteVisitors }>("GET", "/analytics/site-visitors"),
  combined: (days?: number) =>
    request<{ data: CombinedAnalytics }>("GET", `/analytics/combined${days ? `?days=${days}` : ""}`),
  connect: () => request<{ authUrl: string }>("POST", "/analytics/connect"),
  disconnect: () => request<{ success: boolean }>("POST", "/analytics/disconnect"),
  accounts: () => request<{ data: GAAccount[] }>("GET", "/analytics/accounts"),
  setAccount: (accountId: string, accountName: string, properties?: { name: string; displayName: string }[]) =>
    request<{ success: boolean; linked?: { domain: string; propertyId: string }[] }>("POST", "/analytics/account", { accountId, accountName, properties }),
  properties: () => request<{ data: GAProperty[] }>("GET", "/analytics/properties"),
  setProperty: (propertyId: string) =>
    request<{ success: boolean }>("POST", "/analytics/property", { propertyId }),
  setSiteProperty: (siteId: string, propertyId: string) =>
    request<{ success: boolean }>("POST", "/analytics/site-property", { siteId, propertyId }),
  clearSiteProperty: (siteId: string) =>
    request<{ success: boolean }>("DELETE", `/analytics/site-property/${siteId}`),
  autoProvision: (siteId: string) =>
    request<{ data: { propertyId: string; measurementId?: string; created?: boolean } }>("POST", `/analytics/auto-provision/${siteId}`),
  overview: (days?: number, siteId?: string) =>
    request<{ data: GAOverview }>("GET", analyticsQuery("overview", days, siteId)),
  daily: (days?: number, siteId?: string) =>
    request<{ data: GAMetricRow[] }>("GET", analyticsQuery("daily", days, siteId)),
  topPages: (days?: number, siteId?: string) =>
    request<{ data: GATopPage[] }>("GET", analyticsQuery("top-pages", days, siteId)),
  trafficSources: (days?: number, siteId?: string) =>
    request<{ data: GATrafficSource[] }>("GET", analyticsQuery("traffic-sources", days, siteId)),
};

// ─── Scheduled Tasks (recurring Tasks with cronExpression) ──

/** @deprecated Use Task — recurring tasks are Tasks with cronExpression set */
export type ScheduledTask = Task;

export const scheduledTasks = {
  list: (siteId: string) =>
    request<{ data: Task[] }>("GET", `/scheduled-tasks?siteId=${siteId}`),
  create: (body: {
    siteId: string;
    type: TaskType;
    title: string;
    description?: string;
    cronExpression: string;
    enabled?: boolean;
    assignedAgentId?: string;
    templateId?: string;
    context?: Record<string, unknown>;
  }) => request<{ data: Task }>("POST", "/scheduled-tasks", body),
  update: (id: string, patch: Partial<Task>) =>
    request<{ data: Task }>("PATCH", `/scheduled-tasks/${id}`, patch),
  delete: (id: string) => request<{ success: boolean }>("DELETE", `/scheduled-tasks/${id}`),
  run: (id: string) =>
    request<{ data: { task: Task; type: string; title: string; siteId?: string; domain?: string; agentId?: string | null } }>(
      "POST", `/scheduled-tasks/${id}/run`
    ),
};

// ─── Claude / AI Studio ───────────────────────────────────

export const claude = {
  generate: (body: GenerateRequest) =>
    request<GenerateResponse>("POST", "/claude/generate", body),
  status: (jobId: string) =>
    request<GenerateResponse>("GET", `/claude/status/${jobId}`),
  keywordResearch: (siteId: string) =>
    request<{ data: string[]; taskId?: string }>("POST", "/claude/keyword-research", { siteId }),
  siteStrategy: (siteId: string) =>
    request<{ data: AgentTask }>("POST", "/claude/site-strategy", { siteId }),
};

// ─── Health Check ─────────────────────────────────────────

export interface HealthIssue {
  type: string;
  severity: "critical" | "warning" | "info";
  domain: string;
  slug?: string;
  message: string;
  fixable: boolean;
  details?: Record<string, unknown>;
}

export interface HealthReport {
  timestamp: string;
  duration: number;
  sitesChecked: number;
  pagesChecked: number;
  issues: HealthIssue[];
  fixed: number;
  summary: {
    critical: number;
    warning: number;
    info: number;
    fixable: number;
  };
}

export const healthCheck = {
  run: (siteId?: string, options?: { fix?: boolean; checkLive?: boolean }) => {
    const params = new URLSearchParams();
    if (siteId) params.set("siteId", siteId);
    if (options?.fix) params.set("fix", "true");
    if (options?.checkLive) params.set("checkLive", "true");
    return request<{ data: HealthReport }>("GET", `/health-check?${params.toString()}`);
  },
  fix: (siteId: string) =>
    request<{ data: HealthReport }>("POST", "/health-check/fix", { siteId }),
};

// ─── SEO Checks ──────────────────────────────────────────

export interface LinkIssue {
  type: string;
  severity: "critical" | "warning" | "info";
  slug: string;
  target?: string;
  message: string;
  autoFixable: boolean;
}

export interface LinkCheckResult {
  issues: LinkIssue[];
  stats: { total: number; broken: number; valid: number };
}

export interface CrawlIssue {
  type: string;
  severity: "critical" | "warning" | "info";
  url: string;
  message: string;
}

export interface CrawlCheckResult {
  pages?: { url: string; statusCode: number; title: string; wordCount: number }[];
  issues?: CrawlIssue[];
  error?: string;
}

export interface SeoPageResult {
  slug: string;
  title: string;
  score: number;
  issues: string[];
  keywords: { keyword: string; position: number | null }[];
}

export interface SeoAnalysisResult {
  pages?: SeoPageResult[];
  overallScore?: number;
  error?: string;
}

export interface NavCheckResult {
  suggestedNav: { label: string; href: string }[];
  orphanPages: string[];
  missingPages: string[];
}

export interface AffiliateFixResult {
  fixed: number;
  pages: { slug: string; productsAdded: string[] }[];
}

export interface AnalyticsCheckResult {
  configured: boolean;
  measurementId: string | null;
  gaPropertyId: string | null;
  liveTagDetected: boolean | null;
  issues: string[];
}

export interface DeadPagesResult {
  total: number;
  deleted: number;
  pages?: { slug: string; wordCount: number }[];
}

export interface FullAuditResult {
  linkCheck: LinkCheckResult;
  navCheck: NavCheckResult;
  healthCheck: HealthReport;
  analyticsCheck?: AnalyticsCheckResult;
  deadPages?: DeadPagesResult;
  disclaimersFixed?: number;
}

export interface GSCSetupResult {
  siteUrl: string;
  propertyAdded: boolean;
  sitemapSubmitted: boolean;
  sitemapUrl: string;
  error?: string;
}

export interface IndexingRequestResult {
  total: number;
  succeeded: number;
  failed: number;
  results: { url: string; status: "ok" | "error"; error?: string }[];
  truncated?: boolean;
  totalUrls?: number;
}

export interface IndexingAllResult {
  totalSites: number;
  totalUrls: number;
  totalSucceeded: number;
  totalFailed: number;
  sites: { domain: string; urlCount: number; succeeded: number; failed: number }[];
}

export interface URLInspectionResult {
  url: string;
  verdict?: string;
  coverageState?: string;
  indexingState?: string;
  lastCrawlTime?: string;
  crawledAs?: string;
  robotsTxtState?: string;
  error?: string;
}

export const seo = {
  linkCheck: (siteId: string) =>
    request<{ data: LinkCheckResult }>("GET", `/seo/link-check?siteId=${siteId}`),
  crawlCheck: (siteId: string) =>
    request<{ data: CrawlCheckResult }>("POST", "/seo/crawl-check", { siteId }),
  seoAnalysis: (siteId: string) =>
    request<{ data: SeoAnalysisResult }>("POST", "/seo/seo-analysis", { siteId }),
  navCheck: (siteId: string) =>
    request<{ data: NavCheckResult }>("GET", `/seo/nav-check?siteId=${siteId}`),
  fixAffiliates: (siteId: string) =>
    request<{ data: AffiliateFixResult }>("POST", "/seo/fix-affiliates", { siteId }),
  fullAudit: (siteId: string, options?: { fix?: boolean; checkLive?: boolean }) =>
    request<{ data: FullAuditResult }>("POST", "/seo/full-audit", { siteId, ...options }),
  fixNav: (siteId: string) =>
    request<{ data: { navLinks: { label: string; href: string }[] } }>("POST", "/seo/fix-nav", { siteId }),
  // GSC + Indexing
  gscSetup: (siteId: string) =>
    request<{ data: GSCSetupResult }>("POST", "/seo/gsc-setup", { siteId }),
  gscSetupAll: () =>
    request<{ data: { total: number; succeeded: number; results: GSCSetupResult[] } }>("POST", "/seo/gsc-setup-all"),
  gscProperties: () =>
    request<{ data: { siteUrl: string; permissionLevel: string }[] }>("GET", "/seo/gsc-properties"),
  requestIndexing: (siteId: string, slugs?: string[]) =>
    request<{ data: IndexingRequestResult }>("POST", "/seo/request-indexing", { siteId, ...(slugs ? { slugs } : {}) }),
  requestIndexingAll: () =>
    request<{ data: IndexingAllResult }>("POST", "/seo/request-indexing-all"),
  inspectUrl: (siteId: string, slug: string) =>
    request<{ data: URLInspectionResult }>("POST", "/seo/inspect-url", { siteId, slug }),
  siteAuditSummary: (siteId: string, limit = 20) =>
    request<{ data: { siteId: string; pageCount: number; averageScore: number; rewritePriorityCounts: Record<string, number>; topCandidates: Array<{ pageId: string; slug: string; title: string; overallScore: number; rewritePriority: string; wordCount: number; scores: Record<string, number>; recommendations: string[] }> } }>("GET", `/seo/site-audit-summary?siteId=${siteId}&limit=${limit}`),
};

// ─── Products ─────────────────────────────────────────────

export interface SuggestedProduct {
  rank: number;
  score: number;
  network: "amazon" | "ebay" | "cj" | "awin";
  title: string;
  description?: string;
  price?: string;
  imageUrl?: string;
  affiliateUrl?: string;
  productUrl?: string;
  merchantName?: string;
  category?: string;
  availability?: string;
  productRef: string;
  isPromoted: boolean;
}

export interface ProductSuggestResult {
  products: SuggestedProduct[];
  query: string;
  networksSearched: string[];
}

export const products = {
  suggest: (
    siteId: string,
    keyword: string,
    options?: { context?: string; limit?: number; networks?: string[] }
  ) =>
    request<{ data: ProductSuggestResult }>("POST", "/products/suggest", {
      siteId,
      keyword,
      ...options,
    }),
};

// ─── Publishing Policies ──────────────────────────────────

export interface PublishPolicy {
  _id: string;
  tenantId: string;
  siteId: string;
  enabled: boolean;
  dailyQuota: number;
  runTimeLocal: string;
  timezone: string;
  jitterSeconds?: number;
  networks: ("amazon" | "ebay")[];
  categories: string[];
  lastRunDate?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PublishPolicyBody {
  siteId?: string;
  dailyQuota?: number;
  runTimeLocal?: string;
  timezone?: string;
  jitterSeconds?: number;
  networks?: ("amazon" | "ebay")[];
  categories?: string[];
}

export const publishingPolicies = {
  getBySite: (siteId: string) =>
    request<PublishPolicy | null>("GET", `/publishing-policies/site/${encodeURIComponent(siteId)}`),
  create: (body: PublishPolicyBody & { siteId: string }) =>
    request<PublishPolicy>("POST", "/publishing-policies", body),
  update: (id: string, patch: PublishPolicyBody) =>
    request<PublishPolicy>("PATCH", `/publishing-policies/${encodeURIComponent(id)}`, patch),
  pause: (id: string) =>
    request<PublishPolicy>("POST", `/publishing-policies/${encodeURIComponent(id)}/pause`),
  resume: (id: string) =>
    request<PublishPolicy>("POST", `/publishing-policies/${encodeURIComponent(id)}/resume`),
};

// ─── Publish Runs ─────────────────────────────────────────

export interface PublishRunCounts {
  sourced?: number;
  generated?: number;
  published: number;
  skipped: number;
  failed: number;
}

// Mirror of PublishRunStatus from @aas/shared (types/index.ts) — keep in sync
export type PublishRunStatus =
  | "scheduled"
  | "claimed"
  | "enqueued"
  | "sourcing"
  | "generating"
  | "optimizing"
  | "interlinking"
  | "deploying"
  | "complete"
  | "failed";

export interface PublishRun {
  _id: string;
  siteId: string;
  tenantId: string;
  status: PublishRunStatus;
  counts: PublishRunCounts;
  clusterId?: string;
  scheduledFor?: string;
  runDate: string;
  errorSummary?: string;
  createdAt: string;
  updatedAt: string;
  haltReason?: "BUDGET_EXCEEDED" | "AUTH_ERROR" | "QUOTA_EXCEEDED";
  budgetUsedUsd?: number;
  budgetCapUsd?: number;
}

export interface PublishAlert {
  _id: string;
  siteId: string;
  tenantId: string;
  status: "open" | "acknowledged";
  type: "missed-run" | "failed-run";
  clusterId?: string;
  runDate: string;
  createdAt: string;
  updatedAt: string;
}

export const publishRuns = {
  list: (siteId: string, limit?: number) => {
    const params = new URLSearchParams();
    params.set("siteId", encodeURIComponent(siteId));
    if (limit !== undefined) params.set("limit", String(limit));
    return request<{ data: PublishRun[] }>("GET", `/publish-runs?${params.toString()}`);
  },
};

export const publishAlerts = {
  list: (siteId: string) =>
    request<{ data: PublishAlert[] }>(
      "GET",
      `/publish-runs/alerts?siteId=${encodeURIComponent(siteId)}`
    ),
  acknowledge: (id: string) =>
    request<PublishAlert>("POST", `/publish-runs/alerts/${encodeURIComponent(id)}/acknowledge`),
};

// ─── Dead Letter Queue ────────────────────────────────────

export interface DLQErrorHistoryItem {
  error: string;
  attemptNumber: number;
  occurredAt: string;
}

export interface DLQEntry {
  _id: string;
  originalItemId: string;
  queueName: string;
  taskId: string;
  tenantId: string;
  errorHistory: DLQErrorHistoryItem[];
  deadAt: string;
  acknowledgedBy?: string;
  acknowledgedAt?: string;
  requeuedAt?: string;
}

export const dlq = {
  list: (opts?: { queueName?: string; acknowledged?: boolean; siteId?: string }) => {
    const params = new URLSearchParams();
    if (opts?.queueName) params.set("queueName", opts.queueName);
    if (opts?.acknowledged !== undefined) params.set("acknowledged", String(opts.acknowledged));
    if (opts?.siteId) params.set("siteId", opts.siteId);
    const qs = params.toString();
    return request<{ data: DLQEntry[] }>("GET", qs ? `/dlq?${qs}` : "/dlq");
  },
  get: (id: string) => request<DLQEntry>("GET", `/dlq/${id}`),
  acknowledge: (id: string) => request<DLQEntry>("POST", `/dlq/${id}/acknowledge`),
  requeue: (id: string) => request<DLQEntry>("POST", `/dlq/${id}/requeue`),
  stats: () => request<Record<string, unknown>>("GET", "/dlq/stats"),
};

// ─── MCP Servers ──────────────────────────────────────────

export type McpTransport = "stdio" | "http" | "sse";

export interface McpServer {
  _id: string;
  tenantId: string;
  name: string;
  description?: string;
  transport: McpTransport;
  command?: string;
  url?: string;
  args?: string[];
  env?: Record<string, string>;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export const mcpServers = {
  list: () => request<{ data: McpServer[] }>("GET", "/mcp-servers"),
  get: (id: string) => request<{ data: McpServer }>("GET", `/mcp-servers/${id}`),
  create: (body: Partial<McpServer>) =>
    request<{ data: McpServer }>("POST", "/mcp-servers", body),
  update: (id: string, patch: Partial<McpServer>) =>
    request<{ data: McpServer }>("PATCH", `/mcp-servers/${id}`, patch),
  delete: (id: string) => request<{ success: boolean }>("DELETE", `/mcp-servers/${id}`),
};
